import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface CarrinhoItem {
  id?: number;
  nome?: string;
  quantidade: number;
  imagem?: string;
  saldo?: number;
  valorUnitario?: number;
  valorTotalComDesconto?: number;
  // adicione outros campos que você usa, se quiser tipar melhor
}

@Injectable({ providedIn: 'root' })
export class CarrinhoService implements OnDestroy {
  private _cacheKey = 'carrinho';

  private _items$ = new BehaviorSubject<CarrinhoItem[]>(this.readFromCache());
  items$ = this._items$.asObservable();

  private _count$ = new BehaviorSubject<number>(this.calcCount(this._items$.value));
  count$ = this._count$.asObservable();

  private storageListener = (event: StorageEvent) => {
    if (event.key === this._cacheKey) {
      this.refreshFromCache();
    }
  };

  constructor() {
    window.addEventListener('storage', this.storageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener);
  }

  /** Define a chave de cache escopada por empresa */
  setCacheScope(empresaId: string): void {
    const novaChave = empresaId ? `carrinho_${empresaId}` : 'carrinho';
    if (novaChave !== this._cacheKey) {
      this._cacheKey = novaChave;
      this.refreshFromCache();
    }
  }

  get cacheKey(): string {
    return this._cacheKey;
  }

  /** sincroniza a fonte com a lista informada e atualiza cache */
  setItems(items: CarrinhoItem[]) {
    this._items$.next(items);
    this._count$.next(this.calcCount(items));
    localStorage.setItem(this._cacheKey, JSON.stringify(items ?? []));
  }

  /** força re-leitura do cache (útil no bootstrap da app) */
  refreshFromCache() {
    const items = this.readFromCache();
    this._items$.next(items);
    this._count$.next(this.calcCount(items));
  }

  clear() {
    this.setItems([]);
  }

  /** Limpa carrinho + todas as chaves vendas_cache_* do localStorage */
  clearAll() {
    this.clear();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('vendas_cache_') || key.startsWith('carrinho_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    // Remover a chave genérica legada também
    localStorage.removeItem('carrinho');
  }

  // --- helpers ---
  private readFromCache(): CarrinhoItem[] {
    try {
      const raw = localStorage.getItem(this._cacheKey);
      return raw ? (JSON.parse(raw) as CarrinhoItem[]) : [];
    } catch {
      return [];
    }
  }

  private calcCount(items: CarrinhoItem[]): number {
    return (items || []).reduce((acc, it) => acc + (Number(it.quantidade) || 0), 0);
  }
}
